<template>
  <div
    class="PackageToAreaConverter"
    :class="{
      'h-30': design === 'fastorder',
      'flex flex-wrap items-center gap-8': design === 'pdp',
    }"
  >
    <div class="inline-flex gap-8">
      <TextInput
        v-model="neededArea"
        :show-error="errors.length > 0"
        :errors="errors"
        class="w-full max-w-112"
        :class="[{
                   'mobOnly:max-w-80': design === 'minicart',
                   'max-w-88 desk:max-w-96': design === 'fastorder',
                   'mb-12': errors.length > 0, },
                 m2InputClass]"
        :allow-wide-error-mgs="true"
        :select-all-on-click="true"
        :suffix="unitDisplay"
        :design="inputDesign"
        :disabled="disabled"
        :disable-edit="disableEdit"
      />
      <div
        class="overflow-hidden w-full"
        :class="[pcsInputClass]"
      >
        <TouchNumber
          id="amountSelector"
          v-model="thisQuantity"
          :index="100"
          :show-buttons-on-desktop="true"
          :show-units="touchNumberUnit"
          class="w-full"
          :class="{
            'max-w-190': design !== 'fastorder',
            'max-w-160': design === 'fastorder',
            'pointer-events-none bg-bg-inactive text-text-inactive': disableEdit
          }"
          :design="inputDesign"
          :disabled="disabled"
          :disable-edit="disableEdit"
          :attr="attr"
          @change="changeFromAmount"
        />
      </div>
    </div>
    <div v-if="design === 'pdp'" class="mobOnly:flex gap-8">
      <div class="type-headline-sm normal-case leading-single mb-4">
        {{ $t('areaConvert.total') }} {{ packTotalArea }} {{ unitDisplay }}
      </div>
      <div class="type-sm text-text-subtle leading-single">
        {{ oneUnitEquals }}
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ProductVariantModel } from '~/models/productVariant';
import usePackageAreaConverter from '~/composeables/usePackageAreaConverter';
import TextInput from '~/components/form-elements/TextInput.vue';
import NP from 'number-precision';
import TouchNumber from '~/components/form-elements/TouchNumber.vue';
import { useUiStore } from '~/store/ui';
import { CartApiItem } from '~/constants/types/norce';
const { $t } = useNuxtApp();

const uiStore = useUiStore();

NP.enableBoundaryChecking(false);

interface Props {
  input: ProductVariantModel | CartApiItem,
  design?: 'pdp' | 'plpcompact' | 'fastorder' | 'minicart' | 'sm';
  disabled?: boolean,
  disableEdit?: boolean,
  m2InputClass?: string,
  pcsInputClass?: string,
  disableEdit?: boolean,
  attr?: {
    [key: string]: string | number;
  };
}

const props = withDefaults(defineProps<Props>(), {
  design: 'pdp',
  m2InputClass: '',
  pcsInputClass: '',
});

const emit = defineEmits<{
  (e: 'change'): void;
}>();

const totalQuantity = defineModel<number>({ required: true });
const thisQuantity = ref(1);

onMounted(()=> {
  thisQuantity.value = totalQuantity.value;
  // make sure area is correct on mount
  changeFromAmount();
});

const neededArea = ref(0);

const { unitDisplay, areaPerPackage } = usePackageAreaConverter(toRef(()=>props.input));
const placeHolder = props.design === 'plpcompact' ? '' : $t('areaConvert.inputArea.placeholder', { unitDisplay });

const packTotalArea = computed(()=> {
  return NP.times(totalQuantity.value, areaPerPackage.value);
});

const errors = ref<string[]>([]);

const doConversion = () => {
  if (neededArea.value > 0) {
    let needed = NP.divide(neededArea.value, areaPerPackage.value);
    needed = Math.ceil(needed);
    needed = Math.max(needed, 1);
    totalQuantity.value = needed;
    thisQuantity.value = needed;
  }
};

const changeFromAmount = () => {
  // this allows the user to backspace away the number to write
  if (isNaN(thisQuantity.value) || thisQuantity.value < 1) {
    return;
  }
  let newNeededArea = NP.times(thisQuantity.value, areaPerPackage.value);
  neededArea.value = newNeededArea;
};

const inputDesign = computed(()=> {
  if ((props.design === 'plpcompact' && uiStore.isMobile) || props.design === 'fastorder' || props.design === 'minicart' || props.design === 'sm') {
    return 'sm';
  }
  return 'base';
});

/**
 * Fix so we can have different units for different categories.
 * Add to poeeditor, example: units.package.short.Accessories
 */
const touchNumberUnit = computed(() => {
  let output = $t('units.package.short');

  props.input.categories?.forEach((cat) => {
    const key = `units.package.short.${cat.code}`;
    const translation = $t(key);

    if (translation !== key && translation !== '') {
      output = translation;
    }
  });

  return output;
});

const oneUnitEquals = computed(()=> {

  let output = $t('areaConvert.packageEquals', {
    areaPerPackage: areaPerPackage.value,
    unitDisplay,
  });
  props.input.categories?.forEach((cat) => {
    if ($t(`areaConvert.packageEquals.${cat.code}`) !== `areaConvert.packageEquals.${cat.code}` && $t(`areaConvert.packageEquals.${cat.code}`) !== '') {
      output = $t(`areaConvert.packageEquals.${cat.code}`, {
        areaPerPackage: areaPerPackage.value,
        unitDisplay,
      });
    }
  });
  return output;
});

watch(neededArea, (value) => {
  errors.value = [];
  if (isNaN(value) || value < 0) {
    errors.value = ['Please enter a valid number'];
  } else {
    doConversion();
  }
});

watch(totalQuantity, (newVal) => {
  // Sync thisQuantity when totalQuantity changes from parent (backend updates)
  if (newVal !== thisQuantity.value) {
    thisQuantity.value = newVal;
    changeFromAmount();
  }
  emit('change');
});

</script>

<style scoped lang="postcss">
</style>
